import React, {
	useEffect,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
	Shot,
} from '../../../stores/reducers/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import Socket from '../../../connection/socket';
import useShots from '../../../contexts/shots/provider';
import { useToggleAutoplay } from '../../../hooks/use-shot-utilities';
import useAutoplay from '../../../hooks/use-autoplay';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import DurationPicker from '../../general-components/duration-picker';
import usePrevious from '../../../utils/use-previous';
import TooltipWrapper from '../../general-components/tooltips/tooltip-wrapper';

interface ShotDrawerProps {
	shotTabShot: Shot;
	socket: Socket | null;
}

export default function ShotTitleDuration(props: ShotDrawerProps) {
	const {
		shotTabShot,
		socket,
	} = props;

	const media = useSelector((s: GS) => s.MediaReducer.media);

	const shotTitle = shotTabShot?.shot_data?.shotTitle ?? '';
	const [editShotTitle, setEditShotTitle] = useState(shotTitle);

	const {
		currentShot,
		changeShotDuration,
		getShotDuration,
		changeShotTitle,
		lockAutoPlay,
		handleSaveOnEnter,
	} = useShots();

	const prevShotTitle = usePrevious(shotTitle);

	const currentShotId = currentShot.shot_id;

	const handleToggleAutoplay = useToggleAutoplay();

	const {
		updateShotAutoplay,
		showToasterAutoPlayRemoved,
		getIsDurationInputDisabled,
	} = useAutoplay();

	const {
		shot_id: shotId,
		shot_data: shotData,
	} = shotTabShot;

	const {
		shotDuration,
		autoplay,
		autoDuration,
	} = shotData;

	const [
		isDurationInputDisabled,
		durationTooltipMessage,
	] = getIsDurationInputDisabled(shotTabShot);

	const [editDuration, setEditDuration] = useState<number>(0);
	const [editingTitle, setEditingTitle] = useState<boolean>(false);
	const durationRef = useRef<HTMLInputElement | null>(null);
	const autoplayTooltipBackground = useRef<HTMLDivElement | null>(null);
	const autoplayRef = useRef<HTMLButtonElement | null>(null);

	useEffect(() => {
		const getAutoDuration = async (feed: number) => {
			setEditDuration(await getShotDuration(feed));
		};
		if (autoDuration && shotData.shotFeeds[0].media) {
			getAutoDuration(shotData.shotFeeds[0].media);
		} else {
			setEditDuration(shotDuration);
		}
	}, [autoDuration, shotTabShot, shotDuration, shotData, media]);

	const updateShotDurationCB = (seconds: number) => {
		if (seconds === 0 && shotTabShot?.shot_data?.autoplay) {
			showToasterAutoPlayRemoved();
		}

		setEditDuration(seconds);
		changeShotDuration(seconds, shotTabShot, durationRef, false, socket);

		// Change the timer duration when a user updates the shot duration
		updateShotAutoplay(
			shotTabShot,
			seconds - editDuration,
		);
	};

	const autoplayToggleDisabled = lockAutoPlay && currentShotId === shotId;

	const toolTipMessage = (isAutoplay: boolean, disabled: boolean) => {
		if (disabled) { return 'Shot has played'; }
		if (isAutoplay) { return 'Autoplaying next shot'; }
		return 'Manually playing next shot';
	};

	// When input is blurred and title is changed, change local shot title state
	// to passed in prop title once prop changes.
	// Using prev shot title to prevent state flashing back and forth on save.
	useEffect(() => {
		if (shotTitle !== prevShotTitle && !editingTitle) {
			setEditShotTitle(shotTitle);
		}
	}, [editingTitle, prevShotTitle, shotTitle]);

	return (<>
		<div className="flex-align-center justify-content-space-between margin-bottom-medium">
			<div className="grayscale-300">
				<input
					className="bl-font-s1 grayscale-300 background-none border-none margin-right-small height-full ellipses-overflow"
					onBlur={() => {
						setEditingTitle(false);
						changeShotTitle(editShotTitle, shotTabShot.shot_id);
					}}
					onKeyUp={handleSaveOnEnter}
					onClick={() => setEditingTitle(true)}
					placeholder={'Enter Shot Title...'}
					onChange={(e) => setEditShotTitle(e.target.value)}
					value={editShotTitle}
					style={{ width: '239px' }}
					readOnly={!editingTitle}
				/>
			</div>
			<div
				className="duration-tooltip"
				id="autoplay-tooltip-background"
				ref={autoplayTooltipBackground}
			/>
			<TooltipWrapper message={toolTipMessage(autoplay, autoplayToggleDisabled)}>
				<button
					onClick={() => handleToggleAutoplay(shotTabShot)}
					className={`autoplay-toggle ${autoplay ? 'on' : 'off'}`}
					ref={autoplayRef}
					disabled={autoplayToggleDisabled}
				>
					<Icon
						name={autoplay ? ICONS.SHOT_AUTOPLAY_AUTO : ICONS.SHOT_AUTOPLAY_MANUAL}
						size={autoplay ? 16 : 12}
						color={COLORS.WHITE}
					/>
				</button>
			</TooltipWrapper>
			<div className='margin-left-small'/>
			<DurationPicker
				initialDuration={editDuration}
				getDurationCB={updateShotDurationCB}
				isInputDisabled={isDurationInputDisabled}
				tooltipMessage={durationTooltipMessage}
			/>
		</div>
	</>);
}
